import React, { useCallback, useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  arePropertyRatesLoading,
  getCheckoutDate,
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import { initialRooms } from 'lib/offer/occupancyUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import AlwaysOnRateOptionMobile from 'components/OfferPage/AlwaysOn/MobileBookingFlow/AlwaysOnRateOptionMobile'
import BedbankRoomOptionRate from 'components/OfferPage/Bedbank/BedbankRoomOptionRate'
import { getCombinedRates, getRateListKey } from '../singlePropertyUtils'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import usePropertyRatesList from 'hooks/usePropertyRatesList'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { pluralizeToString } from 'lib/string/pluralize'
import Group from 'components/utils/Group'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import LineExclamationCircleIcon from 'components/Luxkit/Icons/line/LineExclamationCircleIcon'
import TextLink from 'components/Luxkit/TextLink'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`
interface Props {
  offer: App.Offer;
  bedbankOffer?: App.BedbankOffer;
  packageViews: Array<OfferPackageOptionView>;
  bedbankViews: Array<App.BedbankOfferPackageView>;
  checkIn?: string;
  checkOut?: string;
  rooms: Array<App.Occupants>;
  flightDestination?: App.Airport;
  previewMode: boolean;
  roomRateId?: string;
  duration?: number;
  airport?: App.Airport;
  roomListId: string;
  isLoading: boolean;
  shouldUseRatesEndpoint: boolean;
  utmContent?: string;
}

function PropertyRoomOptionMobile(props: Props) {
  const {
    offer,
    bedbankOffer,
    packageViews,
    checkIn,
    checkOut,
    rooms,
    flightDestination,
    previewMode,
    roomRateId,
    duration,
    bedbankViews,
    roomListId,
    isLoading,
    shouldUseRatesEndpoint,
    utmContent,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Robert.                                                              */
  /************************************************************************************************/

  const dispatch = useOfferPageDispatch()

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const options = useMemo(() => {
    if (previewMode) {
      // Preview mode is for content people and will show unavailable rates so they can validate them
      // as the rates may not have had prices and such loaded yet
      return packageViews
    }
    const requiresRates = offer.property?.useDynamicCancellationPolicies && !!checkIn && !isLoading
    return packageViews.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
  }, [checkIn, offer, packageViews, previewMode, isLoading])

  const availableOptions = useLPCUniqueOptions(
    offer.id,
    options,
    {
      useDynamicCancellationPolicies: offer.property?.useDynamicCancellationPolicies ?? false,
      duration,
      checkIn,
      rooms: rooms ?? initialRooms,
      showAll: shouldUseRatesEndpoint,
    },
  )

  const [propertyRates] = usePropertyRatesList(offer.property?.id, {
    checkIn,
    checkOut,
    occupancies: rooms ?? initialRooms,
    dynamicAvailability: offer.property?.useDynamicAvailability,
    disabled: !shouldUseRatesEndpoint,
  })

  const displayRates = useMemo(() => {
    const combinedRates = getCombinedRates(
      packageViews,
      bedbankViews,
      availableOptions,
      {
        previewMode,
        showAll: shouldUseRatesEndpoint,
        roomRateId,
        utmContent,
      },
    )

    if (shouldUseRatesEndpoint && checkIn && checkOut) {
      const rateIds = new Set(propertyRates.map(rate => rate.key))
      return combinedRates.filter(rate => rateIds.has(getRateListKey(rate)))
    }
    return combinedRates
  }, [availableOptions, bedbankViews, checkIn, checkOut, packageViews, previewMode, propertyRates, roomRateId, shouldUseRatesEndpoint, utmContent])

  const rateListId = useMemo(() => uuidV4(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [bedbankViews]) // regenerate id if packages change

  const handleChangeCapacityClick = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [dispatch])

  const handleChangeDatesClick = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [dispatch])

  return (
    <Pane background="neutral-six">
      <Group direction="vertical" gap={8}>
        {!displayRates.length && <OptionsContainer>
          <Group direction="vertical" gap={8} noDisplayWhenEmpty>
            {!roomFitsCapacity && <MessageBanner
              kind="informational-primary"
              icon={<LineExclamationCircleIcon />}
              heading="This room cannot accomodate your number of guests"
              description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleChangeCapacityClick}>add more rooms</TextLink></>}
            />}
            {!datesAvailable && <MessageBanner
              kind="informational-primary"
              icon={<LineExclamationCircleIcon />}
              heading="This room is sold out on your dates or max number of guests exceeded"
              description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>try different dates</TextLink></>}
            />}
          </Group>
        </OptionsContainer>}
        {displayRates.length > 1 && (
          <OptionsContainer>
            <BodyText variant="small" colour="neutral-three">{pluralizeToString('package option', displayRates.length)} available</BodyText>
          </OptionsContainer>
        )}
        {displayRates.map((view, index) => {
          if (view.lpcRate) {
            return <Pane key={view.lpcRate.package.uniqueKey}>
              <AlwaysOnRateOptionMobile
                offer={offer}
                optionNumber={index + 1}
                packageView={view.lpcRate}
                flightDestination={flightDestination}
                showOptionLabel={displayRates.length > 1}
              />
            </Pane>
          } else if (view.bedbankRate && bedbankOffer) {
            return <Pane key={view.bedbankRate.rate.id}>
              <BedbankRoomOptionRate
                offer={bedbankOffer}
                packageView={view.bedbankRate.view}
                rate={view.bedbankRate.rate}
                optionNumber={index + 1}
                roomPosition={index + 1}
                roomListId={roomListId}
                rateListId={rateListId}
                showOptionLabel={displayRates.length > 1}
              />
            </Pane>
          }
          return null
        })}
      </Group>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  checkOut: getCheckoutDate(state),
  duration: state.duration,
  previewMode: appState.system.previewMode,
  roomRateId: state.roomOption?.roomRateId,
  airport: state.airport,
  isLoading: arePropertyRatesLoading(state, appState),
  shouldUseRatesEndpoint:
    !!appState.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.accomUsePropertyPageRatesEndpoint],
  utmContent: appState.router.location.query.utm_content,
}), PropertyRoomOptionMobile)
